import React, { useEffect, useState } from "react";
import {
  GraduationCap,
  Users2,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  FileSignature,
  TrendingUp,
  Briefcase,
  CalendarCheck,
  Award,
} from "lucide-react";
import { PageHeader } from "../layout/Shell";
import { Badge } from "../ui/badge";
import { Kpi, Panel, ItemList, PageLink } from "./widgets";
import { api } from "../../lib/api";

// ---------------------------------------------------------------------------
// Programme Manager — owns programmes & cohorts
// ---------------------------------------------------------------------------
export function ProgrammeManagerDashboard({ inst, m }) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!inst) return;
    api.get(`/dashboard/${inst.id}`).then((r) => setCounts(r.data?.counts || {})).catch(() => {});
  }, [inst?.id]);

  return (
    <div data-testid="dashboard-programme-manager">
      <PageHeader
        eyebrow="Programme Operations"
        title={`${inst.short_name} · Programme Manager`}
        description="Cohorts, milestones and faculty allocation at a glance."
        actions={<Badge className="bg-primary text-primary-foreground">Programme Manager</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Programmes owned" value={m.programmes ?? counts.programmes ?? "—"} icon={GraduationCap} testid="pm-kpi-programmes" />
          <Kpi label="Active courses" value={counts.courses ?? m.courses ?? "—"} icon={ClipboardList} testid="pm-kpi-courses" />
          <Kpi label="Cohort completion" value={m.completion_rate != null ? `${m.completion_rate}%` : "—"} icon={CheckCircle2} testid="pm-kpi-completion" />
          <Kpi label="At-risk learners" value={m.at_risk ?? "—"} icon={AlertTriangle} testid="pm-kpi-atrisk" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="Today" title="Pending content approvals" testid="pm-content-approvals" className="lg:col-span-2"
                 action={<PageLink to="/content-studio" label="Open studio" />}>
            <ItemList
              testid="pm-content-list"
              items={[]}
              empty="No pending approvals."
            />
          </Panel>
          <Panel eyebrow="This week" title="Cohort milestones" testid="pm-milestones">
            <ItemList items={[]} empty="No upcoming milestones." />
          </Panel>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Faculty allocation" title="Coverage by course" testid="pm-faculty-coverage">
            <div className="py-6 text-center text-xs text-muted-foreground">No faculty allocation data available.</div>
          </Panel>
          <Panel eyebrow="Workflows" title="Awaiting your approval" testid="pm-workflows" action={<PageLink to="/workflows" label="Open queue" />}>
            <ItemList items={[]} empty="No workflows awaiting approval." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registrar — owns enrolments, certificates, tickets
// ---------------------------------------------------------------------------
export function RegistrarDashboard({ inst, m }) {
  const [tickets, setTickets] = useState([]);
  const [notifs, setNotifs] = useState([]);
  useEffect(() => {
    if (!inst) return;
    api.get(`/tickets/${inst.id}`, { params: { status: "open" } }).then((r) => setTickets(r.data || [])).catch(() => setTickets([]));
    api.get(`/notifications`, { params: { limit: 6 } }).then((r) => setNotifs(r.data?.items || [])).catch(() => setNotifs([]));
  }, [inst?.id]);

  return (
    <div data-testid="dashboard-registrar">
      <PageHeader
        eyebrow="Records & Enrolment"
        title={`${inst.short_name} · Registrar`}
        description="Open tickets, certificate batches and enrolment requests."
        actions={<Badge className="bg-primary text-primary-foreground">Registrar</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Open tickets" value={tickets.length} icon={FileSignature} testid="reg-kpi-tickets" />
          <Kpi label="Enrolled learners" value={(m.students ?? m.learners)?.toLocaleString() ?? "—"} icon={Users2} testid="reg-kpi-enrolled" />
          <Kpi label="Certificates · 30d" value="—" icon={Award} testid="reg-kpi-certs" />
          <Kpi label="Cohorts in flight" value={m.programmes ?? "—"} icon={CalendarCheck} testid="reg-kpi-cohorts" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="Inbox" title="Open support tickets" testid="reg-tickets" className="lg:col-span-2"
                 action={<PageLink to="/student-assistant#tickets" label="Open queue" />}>
            <ItemList
              testid="reg-tickets-list"
              empty="No open tickets — you're clear."
              items={tickets.slice(0, 6).map((t) => ({
                id: t.id,
                title: t.subject,
                meta: `${t.learner_name || "Learner"} · ${t.category} · ${t.severity}`,
                right: new Date(t.ts).toLocaleDateString(),
              }))}
            />
          </Panel>
          <Panel eyebrow="Live" title="Notifications" testid="reg-notifs">
            <ItemList
              items={notifs.slice(0, 5).map((n) => ({
                id: n.id,
                title: n.title,
                meta: n.kind?.replace(".", " · "),
                right: new Date(n.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              }))}
            />
          </Panel>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Certificate batches" title="Pending issuance" testid="reg-cert-batches">
            <ItemList items={[]} empty="No certificate batches pending." />
          </Panel>
          <Panel eyebrow="Enrolment flow" title="Requests this week" testid="reg-enrolment">
            <div className="py-6 text-center text-xs text-muted-foreground">No enrolment request data available.</div>
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Career Services — placement & employer engagement
// ---------------------------------------------------------------------------
export function CareerServicesDashboard({ inst, m }) {
  const placementRate = m.placement_rate ?? null;
  const highestPkg = m.highest_package_lpa ?? null;
  const avgPkg = m.average_package_lpa ?? null;
  const employers = m.companies_recruiting ?? null;
  const alumni = m.alumni_network ?? null;
  const currency = inst.country === "India" ? "₹" : "US$";
  const offerLabel = highestPkg
    ? `${currency}${highestPkg} LPA`
    : avgPkg
    ? `${currency}${avgPkg}`
    : "—";
  const offerEyebrow = highestPkg ? "Highest package" : "Median offer";

  return (
    <div data-testid="dashboard-career-services">
      <PageHeader
        eyebrow="Placement & Outcomes"
        title={`${inst.short_name} · Career Services`}
        description={alumni
          ? `${alumni.toLocaleString()} alumni network${placementRate != null ? ` · ${placementRate}% placement rate` : ""}${employers != null ? ` · ${employers}+ recruiters` : ""}.`
          : "Employer pipeline, placement progress and learner readiness."}
        actions={<Badge className="bg-primary text-primary-foreground">Career Services</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Placement rate" value={placementRate != null ? `${placementRate}%` : "—"} icon={Briefcase} testid="cs-kpi-placement" />
          <Kpi label="Active employers" value={employers ?? "—"} icon={Users2} testid="cs-kpi-employers" />
          <Kpi label={offerEyebrow} value={offerLabel} hint={avgPkg ? `Average ${currency}${avgPkg} LPA` : undefined} icon={TrendingUp} testid="cs-kpi-offer" />
          <Kpi label="Open roles" value="—" icon={ClipboardList} testid="cs-kpi-roles" />
        </section>
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="Pipeline" title="Active placement funnel" testid="cs-funnel" className="lg:col-span-2">
            <div className="py-6 text-center text-xs text-muted-foreground">No placement pipeline data available.</div>
          </Panel>
          <Panel eyebrow="This week" title="Employer events" testid="cs-events">
            <ItemList items={[]} empty="No employer events scheduled." />
          </Panel>
        </section>
        <Panel eyebrow="Learner readiness" title="Career-ready cohort" testid="cs-readiness">
          <ItemList items={[]} empty="No readiness data available." />
        </Panel>
      </div>
    </div>
  );
}
